'use client'

import { useEffect, useState } from 'react'

const DISMISS_KEY = 'ps_coupon_dismissed'
const CLAIMED_KEY = 'ps_coupon_claimed'
const DISMISS_MS = 24 * 60 * 60 * 1000
const CLAIMED_MS = 60 * 24 * 60 * 60 * 1000
// Scroll is the primary trigger (owner: no instant offer on landing) — the
// bar earns its slot only after half the page, or a genuinely long dwell.
const SCROLL_FRACTION = 0.5
const IDLE_DELAY = 45_000

function withinWindow(key: string, windowMs: number): boolean {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return false
    const ts = Number(raw)
    return Number.isFinite(ts) && Date.now() - ts < windowMs
  } catch {
    return false
  }
}

function isSuppressed(): boolean {
  return (
    withinWindow(CLAIMED_KEY, CLAIMED_MS) ||
    withinWindow(DISMISS_KEY, DISMISS_MS)
  )
}

function persist(key: string): void {
  try {
    localStorage.setItem(key, String(Date.now()))
  } catch {}
}

export function useCouponTrigger(enabled: boolean): {
  armed: boolean
  dismiss: () => void
  markClaimed: () => void
} {
  const [armed, setArmed] = useState(false)

  useEffect(() => {
    if (!enabled || isSuppressed()) return

    let fired = false
    const cleanup = (): void => {
      window.clearTimeout(timer)
      window.removeEventListener('scroll', onScroll)
    }
    const arm = (): void => {
      if (fired) return
      fired = true
      setArmed(true)
      cleanup()
    }
    const onScroll = (): void => {
      const max = document.body.scrollHeight - window.innerHeight
      if (max <= 0 || window.scrollY / max >= SCROLL_FRACTION) arm()
    }

    const timer = window.setTimeout(arm, IDLE_DELAY)
    window.addEventListener('scroll', onScroll, { passive: true })
    return cleanup
  }, [enabled])

  function dismiss(): void {
    persist(DISMISS_KEY)
    setArmed(false)
  }

  function markClaimed(): void {
    persist(CLAIMED_KEY)
  }

  return { armed, dismiss, markClaimed }
}
